import { useNavigate, useParams } from '@tanstack/react-router';
import { Archive, ArrowLeft, ClipboardCopy, Check } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { useTranscriptionDetail, useArchiveTranscription } from '../hooks/useTranscription.js';
import type { TranscriptionSourceType, TranscriptionStatus } from '../types/transcription.js';

/** @graph-connects none */
const SOURCE_LABEL_KEYS: Record<TranscriptionSourceType, string> = {
  drive_video: 'transcription.source.driveVideo',
  upload: 'transcription.source.upload',
  ai_fassy_import: 'transcription.source.aiFassy',
  google_docs: 'transcription.source.googleDocs',
};

/** @graph-connects none */
const STATUS_CONFIG: Record<TranscriptionStatus, { labelKey: string; color: string }> = {
  pending: { labelKey: 'transcription.status.pending', color: 'var(--color-warning)' },
  processing: { labelKey: 'transcription.status.processing', color: 'var(--color-warning)' },
  completed: { labelKey: 'transcription.status.completed', color: 'var(--color-success)' },
  failed: { labelKey: 'transcription.status.failed', color: 'var(--color-error)' },
};

/**
 * @graph-connects none
 */
function toJstDateTime(iso: string): string {
  const parts = new Intl.DateTimeFormat('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(iso));
  const get = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}/${get('month')}/${get('day')} ${get('hour')}:${get('minute')}`;
}

/**
 * @graph-node {Page}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 文字起こし詳細ページ。completed時はテキスト全文表示+コピー、processing時はスピナー+自動ポーリング、failed時はエラーメッセージ表示。
 * @graph-connects useTranscriptionDetail [calls] 詳細取得hook（processing中は3秒ポーリング）
 * @graph-connects useArchiveTranscription [calls] アーカイブmutation hook
 */
export function TranscriptionDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams({ strict: false }) as { id: string };
  const navigate = useNavigate();
  const { data, isLoading, error } = useTranscriptionDetail(id);
  const archiveMutation = useArchiveTranscription();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!data?.transcript) return;
    await navigator.clipboard.writeText(data.transcript);
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 1500);
  };

  const handleArchive = () => {
    archiveMutation.mutate(id, {
      onSuccess: () => {
        void navigate({ to: '/transcriptions' });
      },
    });
  };

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-3xl px-4 py-10">
        <Card className="p-10 text-center">
          <div className="loading-spinner mx-auto mb-3" />
          <p className="text-sm text-[var(--color-text-muted)]">{t('app.loading')}</p>
        </Card>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mx-auto w-full max-w-3xl px-4 py-10">
        <Card className="p-10 text-center">
          <p className="text-sm text-[var(--color-error)]">
            {error instanceof Error ? error.message : t('transcription.detail.notFound')}
          </p>
        </Card>
      </div>
    );
  }

  const statusConf = STATUS_CONFIG[data.status];

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-10">
      <button
        type="button"
        onClick={() => {
          void navigate({ to: '/transcriptions' });
        }}
        className="mb-4 inline-flex items-center gap-1 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-accent-primary)]"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        {t('common.backToList')}
      </button>

      <h1 className="mb-3 text-2xl font-bold text-[var(--color-text-primary)]">{data.title}</h1>

      <div className="mb-5 flex flex-wrap items-center gap-3">
        <span className="inline-flex items-center rounded-full bg-[var(--color-bg-hover)] px-2 py-0.5 text-xs font-medium text-[var(--color-text-secondary)]">
          {t(SOURCE_LABEL_KEYS[data.sourceType])}
        </span>
        <span
          className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium"
          style={{
            backgroundColor: `color-mix(in srgb, ${statusConf.color} 15%, transparent)`,
            color: statusConf.color,
          }}
        >
          {data.status === 'processing' ? (
            <span className="inline-block h-2.5 w-2.5 animate-spin rounded-full border-[1.5px] border-current border-t-transparent" />
          ) : (
            <span
              className="inline-block h-1.5 w-1.5 rounded-full"
              style={{ backgroundColor: statusConf.color }}
            />
          )}
          {t(statusConf.labelKey)}
        </span>
        <span className="text-sm tabular-nums text-[var(--color-text-muted)]">
          {toJstDateTime(data.createdAt)}
        </span>
      </div>

      {data.status === 'processing' || data.status === 'pending' ? (
        <Card className="p-16 text-center">
          <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-[3px] border-[var(--color-border)] border-t-[var(--color-accent-primary)]" />
          <p className="text-sm font-medium text-[var(--color-text-primary)]">
            {t('transcription.detail.processing')}
          </p>
          <p className="mt-1 text-xs text-[var(--color-text-muted)]">
            {t('transcription.detail.processingHint')}
          </p>
        </Card>
      ) : data.status === 'failed' ? (
        <Card className="border-[var(--color-error)]/20 p-6">
          <p className="text-sm font-medium text-[var(--color-error)]">
            {t('transcription.detail.failed')}
          </p>
          {data.errorMessage ? (
            <p className="mt-2 text-sm text-[var(--color-text-secondary)]">{data.errorMessage}</p>
          ) : null}
        </Card>
      ) : (
        <Card className="p-6">
          <pre className="max-h-[600px] overflow-y-auto whitespace-pre-wrap text-sm leading-relaxed text-[var(--color-text-primary)]">
            {data.transcript}
          </pre>
          <div className="mt-4 flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              leftIcon={
                copied ? (
                  <Check className="h-4 w-4" aria-hidden="true" />
                ) : (
                  <ClipboardCopy className="h-4 w-4" aria-hidden="true" />
                )
              }
              onClick={() => {
                void handleCopy();
              }}
            >
              {copied ? t('common.copied') : t('common.copy')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<Archive className="h-4 w-4" aria-hidden="true" />}
              onClick={handleArchive}
              disabled={archiveMutation.isPending}
            >
              {t('common.archive')}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
